import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import {
  CheckIcon,
  ChevronSelectorVerticalIcon,
  XIcon,
  XCloseIcon,
  Loading01Icon,
  SearchSmIcon,
} from '@untitledui-icons/react/line'
import {
  DialogTrigger,
  Dialog,
  Popover,
  Button as AriaButton,
  ListBox,
  ListBoxItem,
} from 'react-aria-components'
import { cn } from '@/lib/utils'
import { api } from '@/lib/api'

export interface SelectOption {
  id: number
  name: string
}

interface BaseAsyncMultiSelectProps {
  /** Label shown above the select */
  label?: string
  /** Placeholder when no items selected */
  placeholder?: string
  /** Placeholder for the search input */
  searchPlaceholder?: string
  /** API endpoint for searching (uses React Admin format). Required unless staticOptions is provided. */
  searchEndpoint?: string
  /** Static options to use instead of fetching from API. When provided, searchEndpoint is not needed. */
  staticOptions?: SelectOption[]
  /** Message shown when no results found */
  emptyMessage?: string
  /** Icon shown in the button */
  icon?: React.ComponentType<{ className?: string }>
  /** Additional class names */
  className?: string
  /** Whether the component is disabled */
  isDisabled?: boolean
  /** Transform API response items to SelectOption format */
  transformResponse?: (item: Record<string, unknown>) => SelectOption
  /** Custom filter builder for search. Default uses { name: search } */
  buildFilter?: (search: string) => Record<string, unknown>
  /** Field to sort by. Default is 'name' */
  sortField?: string
}

// Standalone mode props (no React Hook Form)
interface StandaloneProps extends BaseAsyncMultiSelectProps {
  value: SelectOption[]
  onChange: (value: SelectOption[]) => void
  control?: never
  name?: never
}

// React Hook Form mode props
interface FormProps<TFieldValues extends FieldValues> extends BaseAsyncMultiSelectProps {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  value?: never
  onChange?: never
  /** Hook for fetching selected items by IDs (for RHF mode) */
  fetchSelectedItems?: (ids: number[]) => Promise<SelectOption[]>
}

export type AsyncMultiSelectProps<TFieldValues extends FieldValues = FieldValues> =
  | StandaloneProps
  | FormProps<TFieldValues>

function isFormMode<TFieldValues extends FieldValues>(
  props: AsyncMultiSelectProps<TFieldValues>
): props is FormProps<TFieldValues> {
  return 'control' in props && props.control !== undefined
}

export function AsyncMultiSelect<TFieldValues extends FieldValues = FieldValues>(
  props: AsyncMultiSelectProps<TFieldValues>
) {
  const { t } = useTranslation()
  const {
    label,
    placeholder,
    searchPlaceholder,
    searchEndpoint,
    staticOptions,
    emptyMessage,
    icon: Icon,
    className,
    isDisabled,
    transformResponse,
    buildFilter,
    sortField = 'name',
  } = props

  // Determine if we're using static options or API fetching
  const useStaticOptions = Boolean(staticOptions)

  const resolvedPlaceholder = placeholder || t('resources.generic.select')
  const resolvedSearchPlaceholder = searchPlaceholder || t('resources.generic.search')
  const resolvedEmptyMessage = emptyMessage || t('resources.dashboard.filters.no_results')

  // React Hook Form integration (if in form mode)
  const formController = isFormMode(props)
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useController({ control: props.control, name: props.name })
    : null

  // State
  const [isOpen, setIsOpen] = useState(false)
  const [options, setOptions] = useState<SelectOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Form mode: maintain internal state that syncs with form controller
  // Standalone mode: we use props.value directly (no duplication)
  const [formSelectedItems, setFormSelectedItems] = useState<SelectOption[]>([])

  // Computed selected items - use props.value directly for standalone mode
  // This eliminates sync issues by avoiding state duplication
  const selectedItems = !isFormMode(props) ? props.value : formSelectedItems

  // Pending selection state - only applied when popover closes
  const [pendingSelection, setPendingSelection] = useState<SelectOption[]>([])

  // Create a stable key from the current value to detect external changes
  // This key is used to sync pendingSelection when the value changes externally
  const externalValueKey = useMemo(() => {
    const items = !isFormMode(props) ? props.value : formSelectedItems
    return items
      .map((v) => v.id)
      .sort((a, b) => a - b)
      .join(',')
  }, [props, formSelectedItems])

  // Sync pendingSelection from external value when popover is closed
  // This ensures that when the popover opens, pendingSelection is already correct
  useEffect(() => {
    if (!isOpen) {
      const currentValue = !isFormMode(props) ? props.value : formSelectedItems
      setPendingSelection(currentValue)
    }
    // Only re-run when externalValueKey changes (not on every props.value reference change)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalValueKey, isOpen])

  // Load selected items when form value changes (form mode only)
  useEffect(() => {
    if (isFormMode(props) && formController) {
      const ids = formController.field.value as number[] | undefined
      if (ids?.length && props.fetchSelectedItems) {
        props.fetchSelectedItems(ids).then((items) => {
          setFormSelectedItems(items)
        })
      } else if (!ids?.length) {
        setFormSelectedItems([])
      }
    }
  }, [formController?.field.value, props])

  // Fetch options from API or filter static options
  const fetchOptions = useCallback(
    async (search: string) => {
      // If using static options, filter them locally
      if (useStaticOptions && staticOptions) {
        const searchLower = search.toLowerCase()
        const filtered = search
          ? staticOptions.filter((opt) => opt.name.toLowerCase().includes(searchLower))
          : staticOptions
        setOptions(filtered)
        return
      }

      // Otherwise, fetch from API
      if (!searchEndpoint) {
        console.error('AsyncMultiSelect: searchEndpoint is required when staticOptions is not provided')
        return
      }

      setIsLoading(true)
      try {
        // Use custom filter builder if provided, otherwise default to name field
        let filter: Record<string, unknown> = {}
        if (search) {
          if (buildFilter) {
            filter = buildFilter(search)
          } else {
            // Default: Use 'name' field with SQL LIKE pattern
            filter.name = `%${search}%`
          }
        }

        const params = {
          filter: JSON.stringify(filter),
          range_: JSON.stringify([0, 50]),
          sort: JSON.stringify([sortField, 'ASC']),
        }

        const response = await api.post(`${searchEndpoint}/ra/getList`, null, { params })
        const rawData = response.data?.data || []

        // Transform response if a transform function is provided
        const data = transformResponse
          ? rawData.map((item: Record<string, unknown>) => transformResponse(item))
          : rawData

        setOptions(data)
      } catch (error) {
        console.error('Failed to fetch options:', error)
        setOptions([])
      } finally {
        setIsLoading(false)
      }
    },
    [searchEndpoint, staticOptions, useStaticOptions, transformResponse, buildFilter, sortField]
  )

  // Handle popover open/close
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        // Opening: reset search and sync pending selection from current value
        // Use props.value directly for standalone mode to ensure we get the latest value
        setSearchTerm('')
        const currentValue = !isFormMode(props) ? props.value : formSelectedItems
        setPendingSelection(currentValue)
        fetchOptions('')
      } else {
        // Closing: apply pending selection if changed
        const currentValue = !isFormMode(props) ? props.value : formSelectedItems
        const hasChanged =
          pendingSelection.length !== currentValue.length ||
          pendingSelection.some((p) => !currentValue.some((v) => v.id === p.id))

        if (hasChanged) {
          if (isFormMode(props) && formController) {
            // Update React Hook Form
            formController.field.onChange(pendingSelection.map((item) => item.id))
            setFormSelectedItems(pendingSelection)
          } else if (!isFormMode(props)) {
            // Update standalone - this triggers re-render with new props.value
            props.onChange(pendingSelection)
          }
        }
      }
      setIsOpen(open)
    },
    [formSelectedItems, pendingSelection, fetchOptions, props, formController]
  )

  // Debounced search (skip debounce for static options - filtering is instant)
  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm)

    if (useStaticOptions) {
      // For static options, filter immediately without debounce
      fetchOptions(newSearchTerm)
      return
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      fetchOptions(newSearchTerm)
    }, 300)
  }

  // Cleanup debounce
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  // Remove a selected item
  const removeOption = (id: number) => {
    setPendingSelection((prev) => prev.filter((v) => v.id !== id))
  }

  // Display text for button
  const getDisplayText = () => {
    if (selectedItems.length === 0) return resolvedPlaceholder
    if (selectedItems.length === 1) return selectedItems[0].name
    return `${selectedItems.length} ${t('resources.generic.selected')}`
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && <label className="text-sm font-medium text-fg-secondary">{label}</label>}

      <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
        <AriaButton
          isDisabled={isDisabled}
          className={cn(
            'inline-flex w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-left text-md shadow-xs transition-colors',
            'dark:bg-gray-900 dark:border-gray-700',
            'hover:border-brand-500 hover:bg-gray-50 dark:hover:bg-gray-800',
            'focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-500/10',
            'disabled:cursor-not-allowed disabled:opacity-50',
            formController?.fieldState.error && 'border-error-500'
          )}
        >
          <div className="flex items-center gap-2 truncate">
            {Icon && <Icon className="size-5 shrink-0 text-fg-quaternary" />}
            <span
              className={cn(
                'truncate',
                selectedItems.length === 0
                  ? 'text-gray-400'
                  : 'text-gray-900 dark:text-white'
              )}
            >
              {getDisplayText()}
            </span>
          </div>
          <ChevronSelectorVerticalIcon className="size-4 shrink-0 text-fg-quaternary" />
        </AriaButton>

        <Popover
          placement="bottom start"
          className={cn(
            'w-[var(--trigger-width)] overflow-hidden rounded-lg border border-gray-300 bg-white shadow-lg',
            'dark:bg-gray-900 dark:border-gray-700',
            'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
            'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95'
          )}
        >
          <Dialog className="outline-none">
            {/* Search input */}
            <div className="border-b border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-900">
              <div className="relative">
                <SearchSmIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                <input
                  autoFocus
                  type="text"
                  placeholder={resolvedSearchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm outline-none placeholder:text-gray-400 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-900"
                />
              </div>
            </div>

            {/* Options list */}
            <div className="max-h-60 overflow-auto bg-white p-1 dark:bg-gray-900">
              {isLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loading01Icon className="size-5 animate-spin text-fg-quaternary" />
                </div>
              ) : options.length === 0 ? (
                <div className="py-6 text-center text-sm text-fg-quaternary">
                  {resolvedEmptyMessage}
                </div>
              ) : (
                <ListBox
                  aria-label="Options"
                  selectionMode="multiple"
                  selectedKeys={new Set(pendingSelection.map((v) => String(v.id)))}
                  onSelectionChange={(keys) => {
                    const selectedIds = new Set(
                      Array.from(keys as Set<string>).map((k) => parseInt(k, 10))
                    )
                    // Merge existing selections with new ones from visible options
                    const existingNotInView = pendingSelection.filter(
                      (v) => !options.some((opt) => opt.id === v.id)
                    )
                    const newFromView = options.filter((opt) => selectedIds.has(opt.id))
                    setPendingSelection([...existingNotInView, ...newFromView])
                  }}
                  className="outline-none"
                >
                  {options.map((option) => {
                    const isSelected = pendingSelection.some((v) => v.id === option.id)
                    return (
                      <ListBoxItem
                        key={option.id}
                        id={String(option.id)}
                        textValue={option.name}
                        className={cn(
                          'flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-sm outline-none transition-colors',
                          isSelected
                            ? 'bg-brand-50 dark:bg-brand-950'
                            : 'bg-white hover:bg-gray-100 dark:bg-gray-900 dark:hover:bg-gray-800'
                        )}
                      >
                        <div
                          className={cn(
                            'flex size-4 shrink-0 items-center justify-center rounded border transition-colors',
                            isSelected
                              ? 'border-brand-600 bg-brand-600 text-white'
                              : 'border-gray-300 dark:border-gray-600'
                          )}
                        >
                          {isSelected && <CheckIcon className="size-3" />}
                        </div>
                        <span
                          className={cn(
                            'truncate',
                            isSelected
                              ? 'font-medium text-brand-700 dark:text-brand-300'
                              : 'text-gray-900 dark:text-gray-100'
                          )}
                        >
                          {option.name}
                        </span>
                      </ListBoxItem>
                    )
                  })}
                </ListBox>
              )}
            </div>

            {/* Selected items as tags */}
            {pendingSelection.length > 0 && (
              <div className="flex items-start gap-2 border-t border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-900">
                <div className="flex flex-1 flex-wrap gap-1.5">
                  {pendingSelection.map((item) => (
                    <span
                      key={item.id}
                      className="inline-flex items-center gap-1 rounded-md bg-brand-50 px-2 py-1 text-sm font-medium text-brand-700 dark:bg-brand-950 dark:text-brand-300"
                    >
                      {item.name}
                      <button
                        onClick={() => removeOption(item.id)}
                        className="rounded-full p-0.5 transition-colors hover:bg-brand-100 dark:hover:bg-brand-900"
                      >
                        <XIcon className="size-3" />
                      </button>
                    </span>
                  ))}
                </div>
                {/* Clear all button */}
                <button
                  onClick={() => setPendingSelection([])}
                  className="shrink-0 rounded-full p-1 text-fg-quaternary transition-colors hover:bg-gray-100 hover:text-fg-secondary dark:hover:bg-gray-800"
                  title={t('resources.generic.clear_all')}
                >
                  <XCloseIcon className="size-4" />
                </button>
              </div>
            )}
          </Dialog>
        </Popover>
      </DialogTrigger>

      {/* Error message for form mode */}
      {formController?.fieldState.error && (
        <span className="text-sm text-error-600">
          {t(formController.fieldState.error.message || 'common.errors.invalid')}
        </span>
      )}
    </div>
  )
}

export default AsyncMultiSelect
